import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import AlertComponent from "../../components/AlertComponent";
import Modal from "../../components/Modal";
import { EmptyState, PageHeader, Panel } from "../../components/Page";
import StatusBadge from "../../components/StatusBadge";
import { apiUrl, getPayload } from "../../services/apiConfig";
import { errorMessage, formatDateTime } from "../../utils/formatters";
import { perfisUsuario } from "../../utils/options";

const emptyUser = {
  nome: "",
  login: "",
  senha: "",
  perfil: "Usuario",
};

function fromUser(user) {
  if (!user) return emptyUser;
  return {
    nome: user.nome ?? "",
    login: user.login ?? "",
    senha: "",
    perfil: user.perfil ?? "Usuario",
  };
}

function UsuarioForm({ initialUser, onCancel, onSave }) {
  const [form, setForm] = useState(() => fromUser(initialUser));
  const editing = Boolean(initialUser);

  const setField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const payload = editing
      ? { nome: form.nome, login: form.login, perfil: form.perfil }
      : { nome: form.nome, login: form.login, senha: form.senha, perfil: form.perfil };
    onSave(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Nome</label>
          <input value={form.nome} onChange={(event) => setField("nome", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" required />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Usuário</label>
          <input value={form.login} onChange={(event) => setField("login", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" autoComplete="username" required />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {!editing && (
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Senha</label>
            <input type="password" value={form.senha} onChange={(event) => setField("senha", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" autoComplete="new-password" minLength={6} required />
          </div>
        )}
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Perfil</label>
          <select value={form.perfil} onChange={(event) => setField("perfil", event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100">
            {perfisUsuario.map((perfil) => <option key={perfil.value} value={perfil.value}>{perfil.label}</option>)}
          </select>
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button type="button" onClick={onCancel} className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
          Cancelar
        </button>
        <button type="submit" className="rounded-md bg-violet-700 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800">
          Salvar usuário
        </button>
      </div>
    </form>
  );
}

function SenhaModal({ usuario, onClose, onSaved }) {
  const [senha, setSenha] = useState("");

  const handleSubmit = (event) => {
    event.preventDefault();
    axios
      .patch(apiUrl(`/usuarios/${usuario.id}/senha`), { novaSenha: senha })
      .then(() => {
        AlertComponent("Pronto", "Senha alterada com sucesso.");
        onSaved();
      })
      .catch((error) => AlertComponent("Erro", errorMessage(error, "Não foi possível alterar a senha."), { icon: "error" }));
  };

  return (
    <Modal title={`Alterar senha de ${usuario.nome}`} onClose={onClose} width="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Nova senha</label>
          <input type="password" value={senha} onChange={(event) => setSenha(event.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100" minLength={6} autoComplete="new-password" required />
        </div>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
            Cancelar
          </button>
          <button type="submit" className="rounded-md bg-violet-700 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800">
            Alterar senha
          </button>
        </div>
      </form>
    </Modal>
  );
}

function PermissoesModal({ usuario, jobs, onClose, onSaved }) {
  const [permissoes, setPermissoes] = useState(() => {
    const result = {};
    (usuario.permissoesJob ?? []).forEach((permissao) => {
      result[permissao.jobAgendadoId] = {
        visualizar: true,
        podeExecutar: Boolean(permissao.podeExecutar),
      };
    });
    return result;
  });

  const setVisualizar = (jobId, checked) => {
    setPermissoes((current) => ({
      ...current,
      [jobId]: {
        visualizar: checked,
        podeExecutar: checked ? Boolean(current[jobId]?.podeExecutar) : false,
      },
    }));
  };

  const setExecutar = (jobId, checked) => {
    setPermissoes((current) => ({
      ...current,
      [jobId]: {
        visualizar: checked ? true : Boolean(current[jobId]?.visualizar),
        podeExecutar: checked,
      },
    }));
  };

  const salvar = () => {
    const payload = {
      permissoes: Object.entries(permissoes)
        .filter(([, permissao]) => permissao.visualizar)
        .map(([jobAgendadoId, permissao]) => ({
          jobAgendadoId: Number(jobAgendadoId),
          podeExecutar: Boolean(permissao.podeExecutar),
        })),
    };

    axios
      .put(apiUrl(`/usuarios/${usuario.id}/permissoes-jobs`), payload)
      .then(() => {
        AlertComponent("Pronto", "Permissões atualizadas.");
        onSaved();
      })
      .catch((error) => AlertComponent("Erro", errorMessage(error, "Não foi possível salvar as permissões."), { icon: "error" }));
  };

  return (
    <Modal
      title={`Permissões de ${usuario.nome}`}
      subtitle="Visualizar libera o job para o usuário. Executar permite disparo manual individual."
      onClose={onClose}
      width="max-w-4xl"
      footer={
        <>
          <button type="button" onClick={onClose} className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
            Cancelar
          </button>
          <button type="button" onClick={salvar} className="rounded-md bg-violet-700 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800">
            Salvar permissões
          </button>
        </>
      }
    >
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full min-w-[680px] text-left">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Job</th>
              <th className="px-4 py-3">Método</th>
              <th className="px-4 py-3">Visualizar</th>
              <th className="px-4 py-3">Executar manualmente</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {jobs.map((job) => {
              const permissao = permissoes[job.id] ?? { visualizar: false, podeExecutar: false };
              return (
                <tr key={job.id}>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">{job.nome}</p>
                    <p className="max-w-sm truncate text-xs text-slate-500">{job.url}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{job.metodoHttp}</td>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={permissao.visualizar} onChange={(event) => setVisualizar(job.id, event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                  </td>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={permissao.podeExecutar} onChange={(event) => setExecutar(job.id, event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
                  </td>
                </tr>
              );
            })}
            {jobs.length === 0 && (
              <tr>
                <td colSpan={4}>
                  <EmptyState title="Nenhum job cadastrado." />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

export default function UsuariosPage() {
  const [usuarios, setUsuarios] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [senhaUser, setSenhaUser] = useState(null);
  const [permissaoUser, setPermissaoUser] = useState(null);
  const [erro, setErro] = useState("");

  const carregar = () => {
    setErro("");
    Promise.all([axios.get(apiUrl("/usuarios")), axios.get(apiUrl("/jobs"), { metadata: { ignoreLoading: true } })])
      .then(([usuariosResponse, jobsResponse]) => {
        setUsuarios(getPayload(usuariosResponse) || []);
        setJobs(getPayload(jobsResponse) || []);
      })
      .catch((error) => setErro(errorMessage(error, "Não foi possível carregar os usuários.")));
  };

  useEffect(() => {
    carregar();
  }, []);

  const filtered = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return usuarios;
    return usuarios.filter((usuario) => [usuario.nome, usuario.login, usuario.perfil].filter(Boolean).some((value) => String(value).toLowerCase().includes(term)));
  }, [filter, usuarios]);

  const openCreate = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const openEdit = (usuario) => {
    setEditingUser(usuario);
    setShowForm(true);
  };

  const saveUser = (payload) => {
    const request = editingUser
      ? axios.put(apiUrl(`/usuarios/${editingUser.id}`), payload)
      : axios.post(apiUrl("/usuarios"), payload);

    request
      .then(() => {
        AlertComponent("Pronto", editingUser ? "Usuário atualizado." : "Usuário cadastrado.");
        setShowForm(false);
        setEditingUser(null);
        carregar();
      })
      .catch((error) => AlertComponent("Erro", errorMessage(error, "Não foi possível salvar o usuário."), { icon: "error" }));
  };

  const toggleStatus = (usuario) => {
    axios
      .patch(apiUrl(`/usuarios/${usuario.id}/status`), { ativo: !usuario.ativo })
      .then(() => carregar())
      .catch((error) => AlertComponent("Erro", errorMessage(error, "Não foi possível alterar o status."), { icon: "error" }));
  };

  const closeAndReload = () => {
    setSenhaUser(null);
    setPermissaoUser(null);
    carregar();
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Usuários"
        subtitle="Cadastro de acessos e permissões individuais por job."
        actions={
          <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 rounded-md bg-violet-700 px-3 py-2 text-sm font-semibold text-white shadow-sm shadow-violet-900/20 transition hover:bg-violet-800">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
            </svg>
            Novo usuário
          </button>
        }
      />

      {erro && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>}

      <Panel
        actions={
          <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Buscar usuário" className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 sm:w-80" />
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Usuário</th>
                <th className="px-4 py-3">Perfil</th>
                <th className="px-4 py-3">Permissões</th>
                <th className="px-4 py-3">Criado em</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((usuario) => (
                <tr key={usuario.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-slate-900">{usuario.nome}</p>
                    <p className="text-xs text-slate-500">{usuario.login}</p>
                  </td>
                  <td className="px-4 py-3"><StatusBadge value={usuario.perfil} /></td>
                  <td className="px-4 py-3 text-sm text-slate-700">{usuario.permissoesJob?.length ?? 0} job(s)</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{formatDateTime(usuario.criadoEm)}</td>
                  <td className="px-4 py-3"><StatusBadge value={usuario.ativo ? "Ativo" : "Inativo"} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button type="button" title="Editar" onClick={() => openEdit(usuario)} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition hover:bg-slate-100 hover:text-slate-950">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m16.86 4.49 2.65 2.65M5 19l4.5-1 9.9-9.9a1.88 1.88 0 0 0-2.65-2.65l-9.9 9.9L5 19Z" />
                        </svg>
                      </button>
                      <button type="button" title="Alterar senha" onClick={() => setSenhaUser(usuario)} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition hover:bg-slate-100 hover:text-slate-950">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.5 10.5V7.5a4.5 4.5 0 0 0-9 0v3m-.75 0h10.5A1.75 1.75 0 0 1 19 12.25v6A1.75 1.75 0 0 1 17.25 20H6.75A1.75 1.75 0 0 1 5 18.25v-6a1.75 1.75 0 0 1 1.75-1.75Z" />
                        </svg>
                      </button>
                      <button type="button" title="Permissões" onClick={() => setPermissaoUser(usuario)} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition hover:bg-slate-100 hover:text-slate-950">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </button>
                      <button type="button" title={usuario.ativo ? "Inativar" : "Ativar"} onClick={() => toggleStatus(usuario)} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-600 transition hover:bg-slate-100 hover:text-slate-950">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6l4 2m5-2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <EmptyState title="Nenhum usuário encontrado." />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>

      {showForm && (
        <Modal title={editingUser ? "Editar usuário" : "Novo usuário"} onClose={() => setShowForm(false)} width="max-w-3xl">
          <UsuarioForm initialUser={editingUser} onCancel={() => setShowForm(false)} onSave={saveUser} />
        </Modal>
      )}

      {senhaUser && <SenhaModal usuario={senhaUser} onClose={() => setSenhaUser(null)} onSaved={closeAndReload} />}
      {permissaoUser && <PermissoesModal usuario={permissaoUser} jobs={jobs} onClose={() => setPermissaoUser(null)} onSaved={closeAndReload} />}
    </div>
  );
}
